import type { PackingCondition } from '@/utils/climateClassification';

export type PackingPriority = 'essential' | 'recommended' | 'optional';

export interface PackingItem {
  name: string;
  description?: string;
  /** Empty = always included. Non-empty = included when ANY condition matches. */
  conditions: PackingCondition[];
  resourcesLink?: string;
  priority: PackingPriority;
}

export interface PackingCategory {
  name: string;
  icon: string; // Lucide icon name
  items: PackingItem[];
}

export const PACKING_CATEGORIES: PackingCategory[] = [
  {
    name: 'Camera Gear',
    icon: 'Camera',
    items: [
      { name: 'Camera body', description: 'DSLR or mirrorless with manual controls', conditions: [], priority: 'essential', resourcesLink: '/resources#gear' },
      { name: 'Wide-angle fast lens', description: '14-24mm, f/2.8 or wider', conditions: [], priority: 'essential', resourcesLink: '/resources#gear' },
      { name: 'Sturdy tripod', description: 'Carbon fiber preferred for weight', conditions: [], priority: 'essential', resourcesLink: '/resources#gear' },
      { name: 'Ball head', description: 'Arca-Swiss compatible', conditions: [], priority: 'essential', resourcesLink: '/resources#gear' },
      { name: 'Remote shutter release / intervalometer', conditions: [], priority: 'essential' },
      { name: 'Lens cleaning kit', description: 'Rocket blower, microfiber cloths, lens pen', conditions: [], priority: 'essential' },
      { name: 'Bahtinov mask', description: 'Precision star focusing aid', conditions: [], priority: 'recommended' },
      { name: 'Multiple memory cards', conditions: [], priority: 'essential' },
      { name: 'Card reader / backup storage', conditions: [], priority: 'recommended' },
      { name: 'UV filter on lens', description: 'Sacrificial protection from sand and spray', conditions: ['desert', 'coastal'], priority: 'essential' },
      { name: 'Sand/dust protection bags', description: 'Ziplock bags or rain sleeves for all gear', conditions: ['desert'], priority: 'essential' },
      { name: 'Full rain sleeve for camera', conditions: ['coastal', 'tropical'], priority: 'essential' },
      { name: 'Dew heater strap', description: 'USB-powered, prevents condensation on lens', conditions: ['tropical', 'coastal'], priority: 'essential' },
      { name: 'Dew shield for lens', conditions: ['tropical', 'coastal'], priority: 'recommended' },
      { name: 'Silica gel packets', description: 'Moisture control in camera bag', conditions: ['tropical', 'coastal'], priority: 'recommended' },
      { name: 'Extra microfiber cloths', description: 'You will use more than you think', conditions: ['coastal'], priority: 'recommended' },
      { name: 'Insulated camera wrap', description: 'Protects batteries from cold, slows heat loss', conditions: ['alpine', 'highAltitude', 'winter'], priority: 'recommended' },
      { name: 'Light pollution filter', conditions: [], priority: 'optional' },
      { name: 'Telephoto lens', description: 'For deep-sky or moon shots', conditions: [], priority: 'optional' },
    ],
  },
  {
    name: 'Star Tracking',
    icon: 'Compass',
    items: [
      { name: 'Star tracker mount', description: 'Sky-Watcher Star Adventurer, iOptron, or similar', conditions: [], priority: 'optional', resourcesLink: '/resources#gear' },
      { name: 'Polar scope', description: 'Illuminated preferred', conditions: [], priority: 'optional' },
      { name: 'Counterweight system', conditions: [], priority: 'optional' },
      { name: 'Baseplate / mounting hardware', conditions: [], priority: 'optional' },
    ],
  },
  {
    name: 'Power',
    icon: 'Battery',
    items: [
      { name: '3+ spare camera batteries', conditions: [], priority: 'essential' },
      { name: 'Battery charger', conditions: [], priority: 'essential' },
      { name: 'USB power bank', conditions: [], priority: 'essential' },
      { name: 'Car charger adapter', conditions: [], priority: 'recommended' },
      { name: 'Spare USB cables', description: 'Every type you use (cables fail in the field)', conditions: [], priority: 'recommended' },
      { name: '4+ spare batteries', description: 'Cold cuts battery life in half per 10C drop', conditions: ['alpine', 'highAltitude', 'winter'], priority: 'essential' },
      { name: '12V portable power station', description: 'For longer sessions with dew heaters or laptops', conditions: ['tropical', 'coastal'], priority: 'recommended' },
    ],
  },
  {
    name: 'Clothing & Warmth',
    icon: 'Shirt',
    items: [
      { name: 'Warm layers for nighttime', description: 'Nights cool dramatically even in summer', conditions: [], priority: 'essential' },
      { name: 'Thermal base layers', description: 'Top and bottom', conditions: ['winter', 'alpine', 'highAltitude'], priority: 'essential' },
      { name: 'Fleece mid-layer', conditions: ['winter', 'alpine', 'highAltitude'], priority: 'essential' },
      { name: 'Windproof outer jacket with hood', conditions: ['winter', 'alpine', 'highAltitude', 'coastal'], priority: 'essential' },
      { name: 'Insulated waterproof boots', conditions: ['winter', 'alpine', 'highAltitude'], priority: 'essential' },
      { name: 'Waterproof boots', conditions: ['coastal'], priority: 'essential' },
      { name: 'Sand-proof footwear', conditions: ['desert'], priority: 'recommended' },
      { name: 'Windproof insulated gloves', description: 'Touchscreen-compatible for camera adjustments', conditions: ['winter', 'alpine', 'highAltitude'], priority: 'essential' },
      { name: 'Balaclava or face covering', conditions: ['alpine', 'highAltitude'], priority: 'essential' },
      { name: 'Neck gaiter / scarf', conditions: ['winter', 'alpine'], priority: 'recommended' },
      { name: 'Quick-dry clothing', conditions: ['tropical'], priority: 'recommended' },
      { name: 'Light layers', description: 'Desert nights drop 30-40F from daytime', conditions: ['desert', 'summer'], priority: 'recommended' },
    ],
  },
  {
    name: 'Warmth & Comfort',
    icon: 'Flame',
    items: [
      { name: 'Chemical hand warmers', description: 'Bulk pack (10+ pairs)', conditions: ['winter', 'alpine', 'highAltitude'], priority: 'essential' },
      { name: 'Insole foot warmers', description: '6+ hours of warmth', conditions: ['winter', 'alpine', 'highAltitude'], priority: 'recommended' },
      { name: 'Keep spare batteries in body pocket', description: 'Body heat keeps them warm and charged', conditions: ['winter', 'alpine', 'highAltitude'], priority: 'essential' },
      { name: 'Emergency blanket / bivy sack', conditions: ['alpine', 'highAltitude'], priority: 'recommended' },
      { name: 'Folding chair or cushion', conditions: [], priority: 'recommended' },
      { name: 'Thermos for hot drink', conditions: ['winter', 'alpine', 'highAltitude', 'fall'], priority: 'recommended' },
      { name: 'Tripod weight / sandbags', description: 'Stabilize against wind', conditions: ['alpine', 'highAltitude', 'coastal'], priority: 'recommended' },
    ],
  },
  {
    name: 'Insect & Sun Protection',
    icon: 'Bug',
    items: [
      { name: 'Picaridin-based insect repellent', description: 'Won\'t damage camera gear (unlike DEET)', conditions: ['summer', 'tropical'], priority: 'essential' },
      { name: 'Permethrin-treated clothing', description: 'Mosquitoes won\'t land; lasts through washes', conditions: ['tropical'], priority: 'essential' },
      { name: 'Head netting', conditions: ['tropical'], priority: 'recommended' },
      { name: 'Sunscreen SPF 50+', conditions: ['desert', 'highAltitude', 'summer'], priority: 'recommended' },
      { name: 'Sun hat and sunglasses', description: 'For daytime scouting', conditions: ['desert', 'highAltitude'], priority: 'recommended' },
    ],
  },
  {
    name: 'Lighting',
    icon: 'Flashlight',
    items: [
      { name: 'Red-light headlamp', description: 'Preserves night vision (essential)', conditions: [], priority: 'essential' },
      { name: 'Backup light source', conditions: [], priority: 'recommended' },
      { name: 'Green laser pointer', description: 'For pointing out objects (check local regulations)', conditions: [], priority: 'optional' },
    ],
  },
  {
    name: 'Planning & Navigation',
    icon: 'Map',
    items: [
      { name: 'Phone with astro apps', description: 'PhotoPills, Stellarium, Star Walk', conditions: [], priority: 'essential' },
      { name: 'Offline maps', description: 'Downloaded for each stop (no cell service at dark sky sites)', conditions: [], priority: 'essential' },
      { name: 'Compass', description: 'Backup for phone; useful for Polaris alignment', conditions: [], priority: 'recommended' },
    ],
  },
  {
    name: 'Hydration & Food',
    icon: 'Droplets',
    items: [
      { name: 'Water', description: '2+ liters minimum', conditions: [], priority: 'essential' },
      { name: 'Extra water + electrolytes', description: 'Critical in heat and at altitude', conditions: ['desert', 'highAltitude'], priority: 'essential' },
      { name: 'Snacks', description: 'High-energy bars, trail mix', conditions: [], priority: 'recommended' },
    ],
  },
  {
    name: 'Safety & Tools',
    icon: 'Wrench',
    items: [
      { name: 'First aid basics', conditions: [], priority: 'recommended' },
      { name: 'Allen keys / multi-tool', description: 'For tripod and mount adjustments', conditions: [], priority: 'recommended' },
      { name: 'Gaffer tape / zip ties', conditions: [], priority: 'recommended' },
    ],
  },
  {
    name: 'Tips & Reminders',
    icon: 'Lightbulb',
    items: [
      { name: 'Bag gear before bringing indoors', description: 'Place camera in airtight bag first to prevent condensation, then let it acclimate slowly to room temperature', conditions: ['winter', 'alpine', 'highAltitude'], priority: 'essential' },
      { name: 'Air out gear after humid sessions', description: 'Prevents mold growth on optics and in camera bag', conditions: ['tropical', 'coastal'], priority: 'essential' },
      { name: 'Acclimatize 1-2 days before shooting', description: 'Altitude sickness degrades decision-making and enjoyment', conditions: ['highAltitude'], priority: 'essential' },
      { name: 'Spray feet with antiperspirant', description: 'Under socks, prevents moisture-induced cold in winter boots', conditions: ['winter', 'alpine'], priority: 'recommended' },
    ],
  },
];

/**
 * Filter the packing list for a set of conditions.
 * Returns categories with only relevant items, removing empty categories.
 */
export function getFilteredPackingList(conditions: PackingCondition[]): PackingCategory[] {
  const conditionSet = new Set(conditions);

  return PACKING_CATEGORIES
    .map(category => ({
      ...category,
      items: category.items.filter(item => {
        // Always-included items (no conditions)
        if (item.conditions.length === 0) return true;
        // Include if ANY of the item's conditions match
        return item.conditions.some(c => conditionSet.has(c));
      }),
    }))
    .filter(category => category.items.length > 0);
}
